import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  createContext
} from 'react';
import deepmerge from 'deepmerge';

import { setFromSession } from 'wtb-utils';
import { useDialogNavigationContext, useTransactionContext } from 'context';
import { ANALYTICS_EVENTS, SIGN_STEPS } from 'constants.js';
import { useSendAnalyticsEvent } from 'hooks';

const SignContext = createContext();

const defaultState = {
  message: null,
  successCallback: null,
  errorCallback: null,
  sessionUrl: null,
  launchUrl: null,
  qr: null,
  phoneSignValue: null,
  resultCallbackNavigation: null
};

const SignProvider = ({ children, initialState: state = {} }) => {
  const initialState = deepmerge(defaultState, state);

  const { setDialogNavigationPath, setOnClose } = useDialogNavigationContext();
  const { customer } = useTransactionContext();

  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const [ message, setMessage ] = useState(initialState.message);
  const [ successCallback, setSuccessCallback ] = useState(initialState.successCallback);
  const [ errorCallback, setErrorCallback ] = useState(initialState.errorCallback);
  const [ resultCallbackNavigation, setResultCallbackNavigation ] = useState(initialState.resultCallbackNavigation);
  const [ sessionUrl, setSessionUrl ] = useState(initialState.sessionUrl);
  const [ launchUrl, setLaunchUrl ] = useState(initialState.launchUrl);
  const [ qr, setQR ] = useState(initialState.qr);
  // Shared by Open, QR and Wait to reuse pending requests and reconcile after remounts.
  const completeSignRequestRef = useRef(null);

  // SMS only
  const [ phoneSignValue, setPhoneSignValue ] = useState(initialState.phoneSignValue || customer.phone);

  const close = useCallback(() => {
    setSuccessCallback(null);
    setErrorCallback(null);
    setMessage(null);
  }, []);

  const setStep = useCallback((step) => {
    setDialogNavigationPath(`sign/${step}`);

    // Send corresponding analytics event
    switch (step) {
      case SIGN_STEPS.OPEN:
        sendAnalyticsEvent(ANALYTICS_EVENTS.SIGN.OPEN);
        break;
      case SIGN_STEPS.WAIT:
        sendAnalyticsEvent(ANALYTICS_EVENTS.SIGN.WAIT);
        break;
      default: break;
    }
  }, [ setDialogNavigationPath, sendAnalyticsEvent ]);

  useEffect(() => {
    setOnClose(close);
  }, [ close, setOnClose ]);

  const _setSuccessCallback = useCallback((func) => {
    // console.log('[SignContext][_setSuccessCallback] invoked');
    setSuccessCallback(() => {
      if (resultCallbackNavigation) {
        // console.log('[SignContext][_setSuccessCallback] resultCallbackNavigation', resultCallbackNavigation);
        setAppStep(resultCallbackNavigation);
        return;
      }

      return func;
    });
  }, []);

  const _setErrorCallback = useCallback((func) => {
    setErrorCallback(() => {
      if (resultCallbackNavigation) {
        setAppStep(resultCallbackNavigation);
        return;
      }

      return func;
    });
  }, []);

  useEffect(() => {
    let interval;

    if (qr) {
      interval = setInterval(() => {
        setQR((prevQR) => {
          const url = new URL(prevQR);
          const searchParams = new URLSearchParams(url.search);
          searchParams.set('refetch', Math.random() * 100);

          return `${url.origin}${url.pathname}?${searchParams.toString()}`;
        });
      }, 2000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [ qr ]);

  useEffect(() => {
    if (!message) return;

    setFromSession('signMessage', JSON.stringify(message));
  }, [ message ]);

  useEffect(() => {
    if (!phoneSignValue && customer?.phone) {
      setPhoneSignValue(customer.phone);
    }
  }, [ customer ]);

  return (
    <SignContext.Provider
      value={{
        message, setMessage,
        successCallback, setSuccessCallback: _setSuccessCallback,
        errorCallback, setErrorCallback: _setErrorCallback,
        resultCallbackNavigation, setResultCallbackNavigation,
        sessionUrl, setSessionUrl,
        launchUrl, setLaunchUrl,
        setSignStep: setStep,
        qr, setQR,
        completeSignRequestRef,

        // SMS only
        phoneSignValue, setPhoneSignValue
      }}
    >
      {children}
    </SignContext.Provider>
  );
};

const SignConsumer = SignContext.Consumer;

const useSignContext = () => {
  const context = useContext(SignContext);

  if (context === undefined) {
    throw new Error('useSignContext must be used within a SignProvider');
  }

  return context;
};

export {
  SignContext, SignProvider, SignConsumer, useSignContext
};
